import type { MessageListResponse } from '@tandemone/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { reportError } from '@/domains/observability'

import { unsendMessage } from './api'
import { conversationMessagesKey, CONVERSATIONS_LIST_KEY } from './keys'

/** unsend (soft-delete) one of the caller's own messages. optimistic:
 * flip the row to the unsent placeholder immediately; the server-side
 * socket broadcast also fires and is a no-op for the sender (the row
 * already matches). on error we roll back. authz is server-side.
 */
export function useUnsendMessage(conversationId: string) {
  const queryClient = useQueryClient()
  const messagesKey = conversationMessagesKey(conversationId)

  return useMutation({
    mutationFn: (messageId: string) => unsendMessage(conversationId, messageId),
    onMutate: async messageId => {
      await queryClient.cancelQueries({ queryKey: messagesKey })
      const previous = queryClient.getQueryData<{
        pages: MessageListResponse[]
        pageParams: unknown[]
      }>(messagesKey)
      const now = new Date().toISOString()
      queryClient.setQueryData<
        { pages: MessageListResponse[]; pageParams: unknown[] } | undefined
      >(messagesKey, old => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            items: page.items.map(message =>
              message.id === messageId
                ? {
                    ...message,
                    body: '',
                    attachments: [],
                    deletedAt: now,
                    deletedByUserName: message.senderUserName
                  }
                : message
            )
          }))
        }
      })
      return { previous }
    },
    onSuccess: () => {
      /** refresh the list cache so the unsent-as-lastMessage placeholder
       * shows for the sender too (server broadcasts via socket.to(room)
       * which excludes the sending socket).
       */
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_LIST_KEY })
    },
    onError: (err, _messageId, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(messagesKey, ctx.previous)
      }
      reportError(err)
    }
  })
}
